import React from "react";
import "./chat-message.scss";

export type ChatMessageSide = "left" | "right";

export interface ChatMessageProps {
  senderName: string;
  timestamp: string;
  message: React.ReactNode;
  side?: ChatMessageSide;
  avatar?: React.ReactNode;
  avatarLabel?: string;
  accessory?: React.ReactNode;
  accessoryAriaLabel?: string;
  metaAction?: React.ReactNode;
  maxWidth?: string;
  className?: string;
  bubbleClassName?: string;
}

const getAvatarLabel = (senderName: string, avatarLabel?: string): string => {
  if (avatarLabel && avatarLabel.trim()) {
    return avatarLabel.trim().slice(0, 2).toUpperCase();
  }

  const initials = senderName
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return initials || "U";
};

export const ChatMessage: React.FC<ChatMessageProps> = ({
  senderName,
  timestamp,
  message,
  side = "left",
  avatar,
  avatarLabel,
  accessory,
  accessoryAriaLabel,
  metaAction,
  maxWidth = "75%",
  className = "",
  bubbleClassName = "",
}) => {
  const resolvedAvatarLabel = getAvatarLabel(senderName, avatarLabel);

  return (
    <div className={`chat-message chat-message--${side} ${className}`}>
      <div className="chat-message__row">
        <div className="chat-message__avatar" aria-hidden="true">
          {avatar ?? (
            <span className="chat-message__avatar-fallback">
              {resolvedAvatarLabel}
            </span>
          )}
        </div>

        <div className="chat-message__content" style={{ maxWidth }}>
          <div className="chat-message__meta">
            <span className="chat-message__sender">{senderName}</span>
            <span className="chat-message__timestamp">{timestamp}</span>
            {metaAction && (
              <span className="chat-message__meta-action">{metaAction}</span>
            )}
          </div>

          {accessory ? (
            <div
              className={`chat-message__bubble-row chat-message__bubble-row--${side}`}
            >
              <div className={`chat-message__bubble ${bubbleClassName}`}>
                {message}
              </div>
              <div
                className="chat-message__accessory"
                {...(accessoryAriaLabel ? { "aria-label": accessoryAriaLabel } : {})}
              >
                {accessory}
              </div>
            </div>
          ) : (
            <div className={`chat-message__bubble ${bubbleClassName}`}>
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
